import { KeyboardEvent, ReactNode } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

// Single list row rendered as a card. See DESIGN.md "List = cards".
// Prefer `to` (renders a real <Link>); use `onClick` only when the row cannot
// be a plain link — it then gets keyboard/focus affordances for a11y.
// Static (non-interactive) card shell, reused by rows that render no hover
// affordance (e.g. artifacts-card).
export const ROW_CARD_STATIC_CLASS =
  "flex items-center gap-3 rounded-lg bg-card px-3 py-2 text-sm";
const BASE = cn(ROW_CARD_STATIC_CLASS, "transition-colors hover:bg-primary/5");

export function RowCard({
  to,
  onClick,
  className,
  children,
}: {
  to?: string;
  onClick?: () => void;
  className?: string;
  children: ReactNode;
}) {
  const cls = cn(BASE, className);

  if (to) {
    return (
      <Link to={to} className={cls}>
        {children}
      </Link>
    );
  }

  if (onClick) {
    // Two guards before whole-card navigation:
    // 1. Portaled overlays (Radix dialog/select/popover content) bubble
    //    through the React tree but live outside the card's DOM subtree —
    //    the containment check ignores them wholesale.
    // 2. Links / form controls inside the card handle themselves.
    const isInner = (target: EventTarget | null) =>
      (target as HTMLElement | null)?.closest(
        "a,button,input,select,textarea,label,[role='menuitem']"
      );
    const isOutsideCard = (e: {
      target: EventTarget | null;
      currentTarget: EventTarget | null;
    }) =>
      !(e.currentTarget as HTMLElement | null)?.contains(e.target as Node);
    const onActivate = (e: {
      target: EventTarget | null;
      currentTarget: EventTarget | null;
    }) => {
      if (isOutsideCard(e) || isInner(e.target)) return;
      onClick();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        if (isOutsideCard(e) || isInner(e.target)) return;
        e.preventDefault();
        onClick();
      }
    };
    return (
      <div
        role="link"
        tabIndex={0}
        onClick={onActivate}
        onKeyDown={onKeyDown}
        className={cn(
          cls,
          "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
        )}
      >
        {children}
      </div>
    );
  }

  return <div className={cls}>{children}</div>;
}

export default RowCard;
